<template>
  <Modal :title="$t('SHOW_COMPANY_PROFILE')">
    <template #content>
      <div class="modal">
        <div class="modal__header"></div>
        <div class="modal__actions">
          <button
            type="button"
            class="btn btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
            @click="close">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="26"
              height="25"
              viewBox="0 0 26 25"
              fill="none">
              <path
                d="M2 23L23.9203 1.07969"
                stroke="white"
                stroke-width="3"></path>
              <path
                d="M24 23L2.07969 1.07969"
                stroke="white"
                stroke-width="3"></path>
            </svg>
          </button>
        </div>
        <div class="modal__content" style="--bg-color: #fff">
          <div class="max-[1024px]:flex flex-col grid grid-cols-[1fr_3fr] gap-5">
            <ProfileInfo
              :read-only="true"
              :profile-data="profileData"
              :is-info="true" />

            <template v-if="team?.length">
              <div class="sales-rep-list">
                <div
                  v-for="seller in team"
                  :key="seller.id"
                  class="sales-rep-list__item"
                  :class="{
                    'sales-rep-list__item--selected':
                      selectedProfileId === seller.cognitoUserId,
                  }"
                  @click="setSelectedProfile(seller.cognitoUserId)">
                  <div class="sales-rep-list__item-avatar">
                    <AvatarComponent v-model="seller.image" class="w-[100px]" />
                  </div>

                  <div class="sales-rep-list__item-name">
                    {{
                      seller.name || `${seller.firstName} ${seller.lastName}`
                    }}
                  </div>
                  <div v-if="seller.title" class="font-bold">
                    {{ seller.title }}
                  </div>
                  <div class="sales-rep-list__item-opinion">
                    <RatingIcon />
                    <p>{{ seller.rating?.toFixed(1) }}</p>
                    <p>
                      {{
                        $t('OPINION_SIZE', {
                          n: seller.opinionsCount || 0,
                        })
                      }}
                    </p>
                  </div>
                </div>
              </div>
            </template>
            <template v-else>
              <div class="h-full flex justify-center items-center">
                <p>
                  <b>
                    {{ $t('COMPANY_NO_TEAM_MEMBERS') }}
                  </b>
                </p>
              </div>
            </template>
          </div>
          <div
            class="modal__content--body"
            style="
              gap: 15px;
              grid-template-columns: 0.4fr 1fr;
              margin: 15px;
            "></div>
          <div class="modal__content--footer">
            <button
              class="btn btn-primary"
              style="width: 100%; gap: 15px"
              :disabled="selectedProfileId === null"
              @click="askForOffer()">
              <AddPersonIcon />
              <p>{{ $t('ASK_FOR_OFFER_V2') }}</p>
            </button>
          </div>
        </div>
      </div>
    </template>
  </Modal>
</template>
<script setup lang="ts">
import ModalProfileAskForOffer from '~/components/Modal/Profile/AskForOffer.vue'
import { useCompanyStore } from '~/stores/companyStore'
import { useModal } from 'vue-final-modal'
import AvatarComponent from '~/components/shared/AvatarComponent.vue'
import RatingIcon from '~/assets/img/icons/star.svg?skipsvgo'
import AddPersonIcon from '~/assets/img/icons/add_person_icon.svg?skipsvgo'
import { storeToRefs } from 'pinia';
import {useAuthStore} from "~/stores/authStore";
const emit = defineEmits<{
  (e: 'update:model-value', modelValue: string): void
  (e: 'closed'): void
}>()

const props = defineProps({
  offerData: {
    type: Object,
    default: () => ({}),
  },
})

const store = useCompanyStore()

const companyId = ref(props.offerData?.cognitoOwnerId)
const profileData: any = ref(props.offerData)
const team: any = ref(props.offerData?.salesReps)

const {t: $t} = useI18n();
const { $toast } = useNuxtApp();
const {authenticated} = storeToRefs(useAuthStore());

const selectedProfileId = ref(null)

const close = () => {
  emit('closed')
}

function setSelectedProfile(userId: string) {
  selectedProfileId.value = userId
}

function goToSalesRepProfile() {
  const localePath = useLocalePath()

  window.open(localePath(`/sales/profile/${selectedProfileId.value}`), '_blank')
  emit('closed')
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function askForOffer() {
    if (!authenticated.value) {
        $toast.info($t("LOG_IN_TO_CONTINUE"));
        return;
    }
    if (props.offerData.hasProfile) {
        goToSalesRepProfile()

        return
    }
    const {open} = useModal({
        component: ModalProfileAskForOffer,
        attrs: {
            profileId: selectedProfileId.value,
        },
    })

    open()
    emit('closed')
}
</script>
<style lang="scss" scoped>
.modal {
  grid-template-rows: initial;
  grid-template-columns: 1fr 3fr;
  max-width: 1080px;
  max-height: 100%;
  grid-template-areas:
    'header header'
    'content content';
}

.profile__name p[data-v-1b9eefb1],
.profile__name input[data-v-1b9eefb1] {
  color: #353535;
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 25px;
  text-align: center;
  background: transparent;
}

.sales-rep-list {
  display: grid;
  grid-auto-flow: row;
  gap: 1.25rem;
  align-items: start;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: 0.1fr;
  max-height: 500px;
  padding: 5px;
  overflow-y: auto;
  &__item {
    font-size: 14px;
    background-color: #f6f6f6;
    padding: 12px;
    border-radius: 20px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 5px;
    width: clamp(auto, 100%, 400px);
    min-height: 240px;
    &--selected {
      outline: 1px solid #353535;
    }

    &-avatar {
      margin-bottom: 10px;
    }

    &-name {
      text-align: left;
      color: #353535;
      font-size: 16px;
      font-style: normal;
      font-weight: 700;
      line-height: 25px;
      background: transparent;
    }

    &-rate {
      color: #353535;
      font-size: 12px;
      font-style: normal;
      font-weight: 400;
      line-height: 25px;
      display: flex;
      gap: 5px;
      align-items: center;
    }

    &-opinion {
      color: #35353580;
      font-size: 14PX;;
      display: flex;
      flex-direction: row;
      justify-content: start;
      padding-top: 20px;
      gap: 8px;
    }
    &:hover {
      box-shadow: 0 0 10px rgba(0, 0, 0, 0.37);
    }
  }
}
@media (max-width: 1496px) {
  .sales-rep-list {
    max-height: 450px;
    gap: 0.75rem;
    &__item {
      font-size: var(--font-xsmall);
      //height: 240px;
      &-avatar {
        margin-bottom: 10px;
      }
      &-name {
        font-size: var(--font-xsmall);
        line-height: 20px;
      }
      &-rate {
        font-size: var(--font-exsmall);
      }
      &-opinion {
        font-size: var(--font-exsmall)
      }
    }
  }
}
@media (max-width: 1280px) {
  .sales-rep-list {
    &__item {
      &-name {
        font-size: var(--font-xsmall);
      }
      &-opinion,
      &-rate {
        font-size: var(--font-exsmall);
      }
    }
  }
}
@media (max-width: 1024px) {
  .modal {
    max-height: 700px;
  }
  .sales-rep-list {
    grid-auto-flow: column;
    overflow-y: scroll;
    padding: 10px 5px;
    &__item {
      max-height: 260px;
      width: 190px;
      &-avatar {
        margin-bottom: 0;
      }
      &-name {
        font-size: 14px;
        line-height: 18px;
      }
    }
  }
}
</style>
